import React, { useState } from "react";
import {
  Backdrop,
  Divider,
  Fade,
  Modal,
  Typography
} from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import BasicInput from "../../Common/BasicInput";
import { addBalanceModalStyle } from "./AddBalanceModalStyle";
import {useDispatch, useSelector} from "react-redux";
import {confirmAlert} from "react-confirm-alert";
import CustomConfirmModal from "../../Billing/BottomPart/CustomConfirmModal";
import {rechargeInstantTopup} from "../../../api/billingApi";
import {fetchDetails} from "../../../actions/billingAction";
import useInputField from "../../../hooks/useInputField";

const AddBalanceModal = ({ open, onModalClose, onCallback }) => {
  const dispatch = useDispatch();
  const refillAmountInput = useInputField({
    initialValue: 10,
    validate: (val) => {
      if (val === '') return true;

      const num = Number(val);
      if (isNaN(num)) return true;
      if (num < 10 || num > 10000) return 'Minimum amount $10';

      if (!/^\d+$/.test(val)) return 'Only whole numbers are allowed';

      return null;

    }
  });
  const classes = addBalanceModalStyle();
  const {userCardInfo} = useSelector((state)=>(state.billingReducer));
  const [submitting, setSubmitting] = useState(false);
  const confirmModalImg = `${process.env.REACT_APP_CDN_LINK}assets/images/releases/billing/1.0.0/card_icon.png`;
  const descriptionInstantTopUpBottom = "The credits will be added to your account as soon as the payment is processed.";
  const descriptionInstantTopUp = `By clicking I agree, you authorize us to charge your card in the amount of $${refillAmountInput.value}.`;
  const descriptionAutoRecharge = "Would you like to enable auto-recharge so you never have to worry about running out of credits?";
  const descriptionAutoRechargeBottom = "*Enabling auto-recharge is highly recommended. If your account runs out of credits, all campaigns and messages will be paused causing a disruption to your account.";

  const handleClose = () => {
    onModalClose();
  };

  const requestAutoRecharge = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
          <CustomConfirmModal
              title="Your payment was successful."
              onClose={() => {
                onClose();
              }}
              handleConfirm={() => {
                onCallback({ enableAutoRecharge: true });
              }}
              closeButtonText="No"
              confirmButtonText="Yes"
              showConfirmButton={true}
              showCloseButton={true}
              description={descriptionAutoRecharge}
              descriptionBottom={descriptionAutoRechargeBottom}
              imgSrc={confirmModalImg}
          />
      ),
      closeOnClickOutside: false,
    });
  };

  const handleInput = (e) => {
    const val = e.target.value;

    if (val === '') {
      refillAmountInput.onChange(e);
      return;
    }

    const isValidFormat = /^\d+$/.test(val);

    const numericVal = Number(val);

    if (isValidFormat && numericVal <= 10000) {
      refillAmountInput.onChange(e);
    }
  };


  const handleSubmit = () => {
    if (!!refillAmountInput.error){
      return;
    }
    confirmAlert({
      customUI: ({ onClose }) => (
          <CustomConfirmModal
              title="Payment Authorization"
              onClose={onClose}
              handleConfirm={async () => {
                try{
                  setSubmitting(true);
                  const response = await rechargeInstantTopup({
                    "form_data": {
                      refill_amount: refillAmountInput.value
                    }
                  });
                  let res = response.data;

                  if (res.data && res.data.status && res.data.status === 'error') {
                    window.showNotification('ERROR', res.data.html ?? 'Something went wrong. Please try again!');
                    setSubmitting(false);
                    return;
                  } else if (res.success !== undefined && res.success === false) {
                    window.showNotification('ERROR', res.message ?? 'Something went wrong. Please try again!');
                    setSubmitting(false);
                    return;
                  }

                  window.showNotification('SUCCESS', response.data.message);

                  if (res.data.auto_recharge_status !== undefined &&
                      !res.data.auto_recharge_status) {
                    requestAutoRecharge();
                  }

                  dispatch(fetchDetails());
                  setSubmitting(false);
                  if (window.updateCurrentCredit) {
                    window.updateCurrentCredit();
                  }

                  onModalClose();
                }catch (err){
                  setSubmitting(false);
                  window.showNotification('ERROR', 'Something went wrong. Please try again!');
                }
              }}
              closeButtonText="Cancel"
              confirmButtonText="I Agree"
              showConfirmButton={true}
              showCloseButton={true}
              description={descriptionInstantTopUp}
              descriptionBottom={descriptionInstantTopUpBottom}
              imgSrc={confirmModalImg}
          />
      ),
      closeOnClickOutside: false,
    });
  };

  return (
    <>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className={classes.modal}
        open={open}
        onClose={onModalClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={open}>
          <div className={classes.paper}>
            <div className={classes.modalHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <img
                  width={50}
                  src={`${process.env.REACT_APP_CDN_LINK}assets/images/releases/billing/1.0.0/card_icon.png`}
                  alt=""
                />

                <div>
                  <Typography
                    style={{
                      height: "unset !important",
                      color: "black",
                      fontWeight: 600,
                    }}
                    variant="h5"
                  >
                    Add Balance
                  </Typography>
                  <div style={{ fontSize: 16, fontWeight: 300 }}>
                    Add money to wallet
                  </div>
                </div>
              </div>
            </div>
            <Divider />

            <div className={classes.modalBody} style={{ minHeight: 200 }}>
              <div>
                <Typography style={{ height: "unset !important" }}>
                  Amount <span style={{ color: "red" }}>*</span>
                </Typography>

                <BasicInput
                  fullWidth
                  value={refillAmountInput.value}
                  onChange={handleInput}
                  required
                  disabled={submitting}
                />
                {refillAmountInput.error && <span className={classes.errorMessage}>{refillAmountInput.error}</span>}
              </div>


              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  backgroundColor: "#fafafc",
                  borderRadius: 8,
                  padding: 16,
                  gap: 8,
                }}
              >
                <InfoOutlinedIcon style={{ color: "#3a414a" }} />

                <div style={{ color: "#3a414a", fontSize: 14}}>
                  Money will be added to your wallet and your card ending in <span style={{ fontWeight: 'bold' }}>{userCardInfo?.customer?.card_number}</span> will be charged
                </div>
              </div>
            </div>

            <div className={classes.modalFooter}>
              <div
                className={classes.actionContainer}
                style={{ display: "flex", alignItems: "center", gap: 16 }}
              >
                <button
                  onClick={handleClose}
                  className={`${classes.button} ${classes.closeButton}`}
                  style={{ padding: "8px 24px" }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className={`${classes.button} ${classes.confirmButton} ${
                      submitting ? classes.disabled : ""
                  }`}
                  disabled={submitting || !!refillAmountInput.error}
                >
                  {submitting ? "Adding..." : "Add"}
                </button>
              </div>
            </div>
          </div>
        </Fade>
      </Modal>
    </>
  );
};

export default AddBalanceModal;
